import { NetworkError, ServerError, UnknownApiError } from './api-error';

/** Manual retry budget per error instance, per ADR 0014. */
export const MAX_RETRY_ATTEMPTS = 3;

export type RetryableError = ServerError | NetworkError | UnknownApiError;

export function isRetryable(err: unknown): err is RetryableError {
  return err instanceof ServerError || err instanceof NetworkError || err instanceof UnknownApiError;
}

export type RetryHandler = {
  readonly attempts: number;
  readonly exhausted: boolean;
  readonly canRetry: boolean;
  run(): Promise<void>;
};

/**
 * Wraps a retryable action with the manual budget and the idempotency guard.
 * Non-idempotent mutations are never auto-eligible — the caller must surface
 * the duplicate-write warning and opt in explicitly. No automatic backoff in
 * V1; retries are user-driven.
 */
export function makeRetryHandler(action: () => Promise<unknown>, opts: { idempotent?: boolean } = {}): RetryHandler {
  const idempotent = opts.idempotent ?? true;
  let attempts = 0;

  return {
    get attempts() {
      return attempts;
    },
    get exhausted() {
      return attempts >= MAX_RETRY_ATTEMPTS;
    },
    get canRetry() {
      return idempotent && attempts < MAX_RETRY_ATTEMPTS;
    },
    async run() {
      if (!this.canRetry) return;
      attempts += 1;
      await action();
    },
  };
}
